import { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import axios from 'axios';
import DashboardLayout from '../components/layout/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { 
  Coins, 
  Check, 
  Sparkles,
  AlertCircle,
  Gift,
  ShoppingCart,
  RefreshCw
} from 'lucide-react';
import { toast } from 'sonner';
import { API_URL } from '../config';
import { purchaseCredits, getProducts, restorePurchases } from '../services/IAPService';
import { useAuth } from '../contexts/AuthContext';

const isNative = window.Capacitor && window.Capacitor.isNativePlatform();

export default function CreditsPage() {
  const { token } = useAuth();
  const [searchParams] = useSearchParams();
  const [credits, setCredits] = useState(null);
  const [iapProducts, setIapProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [purchasing, setPurchasing] = useState(null);
  const [orders, setOrders] = useState([]);

  useEffect(() => {
    fetchData();
    loadIAPProducts();
    
    // Handle PayPal return (web only)
    const success = searchParams.get('success');
    const paymentId = searchParams.get('paymentId');
    const payerId = searchParams.get('PayerID');
    if (success === 'true' && paymentId && payerId) {
      capturePayment(paymentId, payerId);
    }
  }, [searchParams]);

  const fetchData = async () => {
    try {
      const [creditsRes, ordersRes] = await Promise.all([
        axios.get(`${API_URL}/credits`),
        axios.get(`${API_URL}/payments/history`)
      ]);
      setCredits(creditsRes.data);
      setOrders(ordersRes.data.orders || []);
      setError(null);
    } catch (err) {
      setCredits({ credits: 0, free_check_used: false });
      setOrders([]);
      setError('Connettiti per vedere il tuo saldo aggiornato');
    } finally {
      setLoading(false);
    }
  };

  const loadIAPProducts = async () => {
    const products = await getProducts();
    setIapProducts(products);
  };

  const capturePayment = async (paymentId, payerId) => {
    try {
      const response = await axios.post(`${API_URL}/payments/capture?payment_id=${paymentId}&payer_id=${payerId}`);
      toast.success(response.data.message);
      setCredits(prev => ({ ...prev, credits: response.data.credits }));
      fetchData();
    } catch (err) {
      toast.error('Errore nel completamento del pagamento');
    }
  };

  const handlePurchase = async (productId) => {
    if (purchasing) return;
    setPurchasing(productId);

    const purchaseErrorMessages = {
      store_unavailable: 'Store non disponibile. Chiudi e riapri l\'app, poi riprova.',
      product_unavailable: 'Prodotto non disponibile su App Store Connect. Completa la configurazione degli acquisti in-app.',
      validation_error: 'Acquisto completato, ma validazione crediti non riuscita. Contatta il supporto.',
      validation_failed: 'Validazione acquisto non riuscita. Riprova.',
      purchase_error: 'Errore durante l\'acquisto. Riprova.',
      purchase_timeout: 'L\'acquisto ha impiegato troppo tempo. Riprova.',
      not_native: 'Gli acquisti in-app funzionano solo dall\'app installata.',
    };

    try {
      if (isNative) {
        // Su iOS/Android: usa In-App Purchase di Apple/Google
        const result = await purchaseCredits(productId, token);
        
        if (result.success) {
          toast.success(`✅ Acquisto completato! Crediti aggiunti.`);
          fetchData(); // Ricarica il saldo
        } else if (result.error === 'cancelled') {
          // Utente ha annullato, non mostrare errore
        } else if (result.error === 'redirect_to_web') {
          // Già gestito nel servizio
        } else {
          console.warn('[IAP] Acquisto fallito:', result);
          toast.error(purchaseErrorMessages[result.error] || 'Errore durante l\'acquisto. Riprova.');
        }
      } else {
        // Su web: usa PayPal
        const response = await axios.post(`${API_URL}/payments/create-order`, {
          package_id: productId
        });
        window.location.href = response.data.approval_url;
      }
    } catch (err) {
      toast.error('Errore durante l\'acquisto. Riprova.');
    } finally {
      setPurchasing(null);
    }
  };

  const handleRestorePurchases = async () => {
    if (!isNative) return;
    toast.info('Ripristino acquisti in corso...');
    const result = await restorePurchases(token);
    if (result.success) {
      toast.success('Acquisti ripristinati!');
      fetchData();
    } else {
      toast.error('Nessun acquisto da ripristinare.');
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('it-IT', {
      day: '2-digit', month: 'short', year: 'numeric'
    });
  };

  // Usa i prodotti IAP se disponibili, altrimenti fallback
  const displayPackages = iapProducts.length > 0 ? iapProducts : [
    { productId: 'com.bustacontrollo.app.credits5',  title: '5 Crediti',  price: '€4,99',  credits: 5,  popular: false },
    { productId: 'com.bustacontrollo.app.credits10', title: '10 Crediti', price: '€8,99',  credits: 10, popular: true  },
    { productId: 'com.bustacontrollo.app.credits20', title: '20 Crediti', price: '€13,99', credits: 20, popular: false },
    { productId: 'com.bustacontrollo.app.credits50', title: '50 Crediti', price: '€28,99', credits: 50, popular: false },
  ];

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center min-h-[400px]">
          <div className="spinner" />
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="max-w-4xl mx-auto space-y-8" data-testid="credits-page">
        {/* Error banner */}
        {error && (
          <div className="flex items-center gap-3 p-4 bg-amber-50 border border-amber-200 text-amber-800 text-sm rounded-lg">
            <AlertCircle className="w-5 h-5 flex-shrink-0" />
            <span>{error}</span>
          </div>
        )}

        {/* Header */}
        <div className="text-center">
          <h1 className="text-3xl font-bold tracking-tight mb-2">I Tuoi Crediti</h1>
          <p className="text-muted-foreground">Acquista crediti per verificare le tue buste paga</p>
        </div>

        {/* Credit Balance */}
        <Card className="swiss-card bg-gradient-to-br from-forest to-emerald-700 text-white">
          <CardContent className="p-8">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-white/70 text-sm mb-2">Saldo Crediti</p>
                <p className="text-5xl font-bold font-mono">{credits?.credits || 0}</p>
                <p className="text-white/70 text-sm mt-2">
                  {!credits?.free_check_used && (
                    <span className="flex items-center gap-1">
                      <Gift className="w-4 h-4" /> Primo controllo gratuito!
                    </span>
                  )}
                </p>
              </div>
              <div className="w-20 h-20 bg-white/10 rounded-full flex items-center justify-center">
                <Coins className="w-10 h-10" />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Come Funziona */}
        <Card className="swiss-card">
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <Sparkles className="w-5 h-5 text-amber-500" />
              Come Funziona
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div className="text-center p-4">
                <div className="w-12 h-12 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-3">
                  <Gift className="w-6 h-6 text-emerald-600" />
                </div>
                <h3 className="font-semibold mb-1">Primo Gratis</h3>
                <p className="text-sm text-muted-foreground">Il tuo primo controllo è completamente gratuito</p>
              </div>
              <div className="text-center p-4">
                <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-3">
                  <ShoppingCart className="w-6 h-6 text-blue-600" />
                </div>
                <h3 className="font-semibold mb-1">Acquista Crediti</h3>
                <p className="text-sm text-muted-foreground">Compra pacchetti di crediti per verifiche aggiuntive</p>
              </div>
              <div className="text-center p-4">
                <div className="w-12 h-12 bg-purple-100 rounded-full flex items-center justify-center mx-auto mb-3">
                  <Check className="w-6 h-6 text-purple-600" />
                </div>
                <h3 className="font-semibold mb-1">Verifica</h3>
                <p className="text-sm text-muted-foreground">Ogni verifica usa 1 credito e controlla IRPEF, INPS e netto</p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Pacchetti Crediti - su web usa PayPal, su iOS/Android usa IAP */}
        <div>
          <h2 className="text-xl font-bold mb-4">Pacchetti Crediti</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {displayPackages.map((pkg) => {
              const productId = pkg.productId || pkg.id;
              const isPopular = pkg.popular || pkg.credits === 10;
              const isPurchasing = purchasing === productId;

              return (
                <Card
                  key={productId}
                  className={`swiss-card relative ${isPopular ? 'border-2 border-forest' : ''}`}
                >
                  {isPopular && (
                    <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                      <span className="bg-forest text-white text-xs font-bold px-3 py-1 rounded-full">
                        PIÙ POPOLARE
                      </span>
                    </div>
                  )}
                  <CardContent className="p-6">
                    <div className="flex items-center justify-between mb-4">
                      <div>
                        <p className="text-2xl font-bold">{pkg.credits}</p>
                        <p className="text-sm text-muted-foreground">crediti</p>
                      </div>
                      <div className="text-right">
                        <p className="text-xl font-bold text-forest">
                          {pkg.price || pkg.priceString || `€${pkg.priceValue}`}
                        </p>
                        <p className="text-xs text-muted-foreground">
                          €{((pkg.priceValue || parseFloat((pkg.price || '0').replace('€', '').replace(',', '.'))) / pkg.credits).toFixed(2)}/credito
                        </p>
                      </div>
                    </div>
                    <Button
                      className={`w-full ${isPopular ? 'bg-forest hover:bg-forest/90' : ''}`}
                      onClick={() => handlePurchase(productId)}
                      disabled={isPurchasing}
                    >
                      {isPurchasing ? (
                        <span className="flex items-center gap-2">
                          <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                          Acquisto in corso...
                        </span>
                      ) : (
                        <span className="flex items-center gap-2">
                          <ShoppingCart className="w-4 h-4" />
                          Acquista {pkg.credits} Crediti
                        </span>
                      )}
                    </Button>
                  </CardContent>
                </Card>
              );
            })}
          </div>

          {/* Ripristina Acquisti - solo su iOS */}
          {isNative && (
            <div className="mt-4 text-center">
              <button
                onClick={handleRestorePurchases}
                className="text-sm text-muted-foreground underline hover:text-forest"
              >
                <span className="flex items-center justify-center gap-1">
                  <RefreshCw className="w-3 h-3" />
                  Ripristina acquisti precedenti
                </span>
              </button>
            </div>
          )}
        </div>

        {/* Storico ordini */}
        {orders.length > 0 && (
          <Card className="swiss-card">
            <CardHeader>
              <CardTitle className="text-lg">Storico Acquisti</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {orders.map((order, idx) => (
                  <div key={idx} className="flex items-center justify-between py-2 border-b last:border-0">
                    <div>
                      <p className="font-medium">{order.credits} crediti</p>
                      <p className="text-sm text-muted-foreground">{formatDate(order.created_at)}</p>
                    </div>
                    <div className="text-right">
                      <p className="font-semibold">€{order.amount}</p>
                      <span className="text-xs text-emerald-600 bg-emerald-50 px-2 py-0.5 rounded-full">
                        Completato
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}
      </div>
    </DashboardLayout>
  );
}
